import React, { useState } from 'react';
import { ArrowUpRight, Music2 } from 'lucide-react';
import CampaignArtwork from '../../components/CrowdCards/CampaignArtwork';
import {
  artworkUrl,
  campaignStatus,
  campaignMetrics,
  formatUsd,
  formatXlm,
} from '../../components/CrowdCards/campaignPresentation';

// Editorial presentation of the existing catalogue; no additional API calls.
export default function FeaturedMusic({
  campaigns,
  onDetails,
  liveprice,
  hero = false,
}) {
  const [unavailable, setUnavailable] = useState([]);
  const seen = new Set();
  const catalogue = campaigns.filter((el) => {
    const url = artworkUrl(el?.musicId?.bitMapImage);
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
  const available = catalogue.filter(
    (el) => !unavailable.includes(artworkUrl(el.musicId.bitMapImage))
  );
  const selected = available[0] || campaigns[0];
  const artwork = available.length ? selected?.musicId?.bitMapImage : '';
  const title =
    selected?.musicId?.longName || selected?.name || 'Music on TokenizeMe';
  const artist = selected?.featured_by || selected?.musicId?.shareHolderName;
  const metrics = campaignMetrics(selected, liveprice);
  const status = campaignStatus(
    selected,
    campaignMetrics(selected, liveprice).soldOut
  );
  const markUnavailable = (url) =>
    setUnavailable((previous) =>
      previous.includes(url) ? previous : [...previous, url]
    );
  if (hero)
    return (
      <div className="tm-hero-records">
        <div className="tm-record-glow" aria-hidden="true" />
        {available.slice(1, 3).map((el, index) => (
          <div
            key={el._id}
            className={`tm-record-layer tm-record-layer--${index}`}
            aria-hidden="true"
          >
            <CampaignArtwork
              src={el.musicId.bitMapImage}
              title={el.musicId.longName || 'Music'}
            />
          </div>
        ))}
        <button
          className="tm-hero-record"
          onClick={() => selected && onDetails(selected)}
          disabled={!selected}
          aria-label={
            selected ? `Explore featured music: ${title}` : 'Music artwork'
          }
        >
          {status.label && (
            <span className="tm-hero-campaign-status">{status.label}</span>
          )}
          <CampaignArtwork
            src={artwork}
            title={title}
            eager
            onUnavailable={markUnavailable}
          />
          <span className="tm-hero-record-caption">
            <span className="tm-hero-record-kicker">
              FROM THE CATALOGUE <ArrowUpRight size={15} aria-hidden="true" />
            </span>
            <strong>{title}</strong>
            {artist && <span>{artist}</span>}
          </span>
        </button>
        {selected && (
          <div className="tm-hero-financials">
            <div className="tm-hero-progress-label">
              <span>Funding progress</span>
              <strong>
                {metrics.progress !== null
                  ? `${new Intl.NumberFormat('en-US', {
                      maximumFractionDigits: 1,
                    }).format(metrics.progress)}% funded`
                  : 'Unavailable'}
              </strong>
            </div>
            {metrics.progress !== null && (
              <div
                className="tm-funding-track"
                role="progressbar"
                aria-label="Featured offering funding"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.min(100, metrics.progress)}
              >
                <span
                  style={{ width: `${Math.min(100, metrics.progress)}%` }}
                />
              </div>
            )}
            <dl className="tm-funding-values">
              <div>
                <dt>Raised · USD</dt>
                <dd>
                  {metrics.raised !== null ? formatUsd(metrics.raised) : '—'}
                </dd>
                {metrics.xlm !== null && (
                  <small>{formatXlm(metrics.xlm)} XLM</small>
                )}
              </div>
              <div>
                <dt>Target · USD</dt>
                <dd>{metrics.target > 0 ? formatUsd(metrics.target) : '—'}</dd>
                {metrics.targetXlm > 0 && (
                  <small>{formatXlm(metrics.targetXlm)} XLM</small>
                )}
              </div>
            </dl>
            <button
              className="tm-hero-details"
              onClick={() => onDetails(selected)}
            >
              View offering details{' '}
              <ArrowUpRight size={14} aria-hidden="true" />
            </button>
          </div>
        )}
        <span className="tm-record-label">
          <Music2 size={14} aria-hidden="true" /> Music worth exploring.
        </span>
      </div>
    );
  if (!selected) return null;
  return (
    <section className="tm-music-spotlight" aria-label="Featured music">
      <div className="tm-spotlight-copy">
        <span className="tm-discovery-kicker">FEATURED MUSIC</span>
        <h2>
          Behind every offering,
          <br />
          there’s a piece of music.
        </h2>
        <p>
          Explore the sound, the story, and the rights behind an offering. Start
          with the music. Make an informed decision.
        </p>
        <button onClick={() => onDetails(selected)}>
          Explore this offering <ArrowUpRight size={17} aria-hidden="true" />
        </button>
      </div>
      <button
        className="tm-spotlight-art"
        onClick={() => onDetails(selected)}
        aria-label={`View featured offering: ${title}`}
      >
        <CampaignArtwork
          src={artwork}
          title={title}
          onUnavailable={markUnavailable}
        />
        <span className="tm-spotlight-caption">
          <span>
            <strong>{title}</strong>
            {artist && <span>{artist}</span>}
          </span>
          {status.label && (
            <span className="tm-spotlight-status">{status.label}</span>
          )}
        </span>
      </button>
    </section>
  );
}
